import { Skeleton } from '@/components/ui/Skeleton';
import { pageShell } from '@/lib/styles';

export function RouteSkeleton({ pathname }: { pathname: string }) {
  if (pathname === '/') return null;
  if (pathname.startsWith('/painel')) return null;
  if (pathname.startsWith('/imovel/')) return null;
  if (pathname.startsWith('/meus-anuncios')) return null;
  if (pathname.startsWith('/favoritos')) return <ListPageSkeleton />;
  if (pathname.startsWith('/perfil')) return <ProfileRouteSkeleton />;
  if (pathname.startsWith('/anunciar')) return <NewListingRouteSkeleton />;
  if (pathname.startsWith('/editar/')) return <FormPageSkeleton />;
  if (pathname.startsWith('/destacar/')) return null;
  if (pathname.startsWith('/premium')) return <PremiumRouteSkeleton />;
  if (pathname.startsWith('/admin')) return <AdminRouteSkeleton />;
  if (pathname === '/mapa') return <MapSkeleton />;
  if (pathname.startsWith('/login') || pathname.startsWith('/reset-password')) return <AuthSkeleton />;
  return <GenericSkeleton />;
}

export function HomeSkeleton() {
  return (
    <>
      <div className="lg:hidden w-full">
        <Skeleton className="h-[420px] w-full" rounded="none" />
        <div className="relative z-10 mx-auto -mt-12 flex max-w-6xl flex-col gap-4 px-4 pb-8">
          <div className="rounded-2xl p-4 flex items-center gap-3">
            <Skeleton className="h-10 w-10 shrink-0" rounded="xl" />
            <div className="flex-1 flex flex-col gap-2">
              <Skeleton className="h-3 w-28" rounded="md" />
              <Skeleton className="h-4 w-52" rounded="md" />
              <Skeleton className="h-3 w-full" rounded="md" />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-11 w-full" rounded="full" />
            <div className="grid grid-cols-2 gap-2">
              <Skeleton className="h-11 w-full" rounded="full" />
              <Skeleton className="h-11 w-full" rounded="full" />
            </div>
            <Skeleton className="h-11 w-full" rounded="full" />
          </div>
          <div className="grid grid-cols-1 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <PropertyCardSkeletonInline key={i} />
            ))}
          </div>
        </div>
      </div>

      <div className="hidden lg:block w-full">
        <Skeleton className="h-[620px] w-full" rounded="none" />
        <div className="relative z-10 mx-auto -mt-10 flex max-w-6xl flex-col gap-5 px-4 sm:px-6 pb-8">
          <div className="rounded-2xl p-4 flex items-center gap-4">
            <Skeleton className="h-10 w-10 shrink-0" rounded="xl" />
            <div className="flex-1 flex flex-col gap-2">
              <Skeleton className="h-3 w-28" rounded="md" />
              <Skeleton className="h-4 w-56" rounded="md" />
              <Skeleton className="h-3 w-4/5" rounded="md" />
            </div>
            <Skeleton className="h-4 w-4" rounded="full" />
          </div>

          <div className="flex flex-col gap-3">
            <Skeleton className="h-11 w-full" rounded="full" />
            <div className="flex gap-2">
              <Skeleton className="h-11 flex-1 min-w-[120px]" rounded="full" />
              <Skeleton className="h-11 flex-1 min-w-[130px]" rounded="full" />
              <Skeleton className="h-11 flex-1 min-w-[150px]" rounded="full" />
              <Skeleton className="h-11 flex-1 min-w-[140px]" rounded="full" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <PropertyCardSkeletonInline key={i} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function PropertyCardSkeletonInline() {
  return (
    <div className="rounded-2xl border border-line shadow-card overflow-hidden flex flex-col">
      <Skeleton className="aspect-[4/3] w-full" rounded="none" />
      <div className="p-3 flex flex-col gap-2">
        <Skeleton className="h-4 w-2/3" rounded="md" />
        <Skeleton className="h-3 w-1/2" rounded="md" />
        <Skeleton className="h-3 w-3/4" rounded="md" />
      </div>
    </div>
  );
}

export function DashboardRouteSkeleton() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-5 sm:gap-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Skeleton className="h-4 w-56" rounded="md" />
        <Skeleton className="h-10 w-44" rounded="2xl" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-line p-3 sm:p-4 shadow-card flex flex-col gap-2 sm:gap-3">
            <Skeleton className="w-9 h-9" rounded="xl" />
            <Skeleton className="h-6 sm:h-7 w-16" rounded="md" />
            <Skeleton className="h-3 w-20" rounded="md" />
          </div>
        ))}
      </div>
      <div className="rounded-3xl border border-line p-4 sm:p-5 shadow-card">
        <Skeleton className="h-5 w-64 mb-5" rounded="md" />
        <div className="h-[220px] flex items-end gap-2 px-1">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton
              key={i}
              className="flex-1"
              rounded="md"
              style={{ height: `${30 + ((i * 37) % 60)}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdminRouteSkeleton() {
  return (
    <>
      <div className="lg:hidden max-w-6xl mx-auto px-4 py-6 flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-7 w-40" rounded="md" />
          <Skeleton className="h-4 w-56 max-w-full" rounded="md" />
          <div className="grid grid-cols-3 gap-1.5 pt-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" rounded="xl" />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-line p-4 flex flex-col gap-3">
              <Skeleton className="w-9 h-9" rounded="xl" />
              <div className="flex flex-col gap-2">
                <Skeleton className="h-6 w-16" rounded="md" />
                <Skeleton className="h-3 w-20 max-w-full" rounded="md" />
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-28 shrink-0" rounded="xl" />
          ))}
        </div>

        <div className="flex flex-col gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <AdminChartSkeleton key={i} compact />
          ))}
        </div>
      </div>

      <div className="hidden lg:flex max-w-6xl mx-auto px-4 py-8 flex-col gap-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-44" rounded="md" />
            <Skeleton className="h-4 w-64" rounded="md" />
          </div>
          <div className="flex gap-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-20" rounded="xl" />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-line p-4 flex flex-col gap-3">
              <Skeleton className="w-9 h-9" rounded="xl" />
              <div className="flex flex-col gap-2">
                <Skeleton className="h-7 w-20" rounded="md" />
                <Skeleton className="h-3 w-24" rounded="md" />
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-32" rounded="xl" />
          ))}
        </div>

        <div className="flex flex-col gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <AdminChartSkeleton key={i} />
          ))}
        </div>
      </div>
    </>
  );
}

function AdminChartSkeleton({ compact = false }: { compact?: boolean }) {
  return (
    <div className="rounded-3xl border border-line p-4 sm:p-5 flex flex-col gap-5">
      <Skeleton className="h-5 w-48 sm:w-64 max-w-full" rounded="md" />
      <div className={`${compact ? 'h-44' : 'h-56'} flex items-end gap-2 px-1`}>
        {Array.from({ length: compact ? 8 : 14 }).map((_, i) => (
          <Skeleton
            key={i}
            className="flex-1"
            rounded="md"
            style={{ height: `${28 + ((i * 29) % 58)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

export function PropertyDetailSkeleton() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col gap-4">
      <Skeleton className="h-64 sm:h-96 w-full" rounded="2xl" />
      <div className="flex flex-col gap-3">
        <Skeleton className="h-7 w-2/3" rounded="md" />
        <Skeleton className="h-4 w-1/2" rounded="md" />
        <div className="grid grid-cols-3 gap-3 mt-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16" rounded="xl" />
          ))}
        </div>
        <Skeleton className="h-4 w-full mt-3" rounded="md" />
        <Skeleton className="h-4 w-11/12" rounded="md" />
        <Skeleton className="h-4 w-10/12" rounded="md" />
      </div>
    </div>
  );
}

function ListPageSkeleton() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col gap-4">
      <Skeleton className="h-7 w-48" rounded="md" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <PropertyCardSkeletonInline key={i} />
        ))}
      </div>
    </div>
  );
}

function FormPageSkeleton() {
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-4">
      <Skeleton className="h-7 w-56" rounded="md" />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-3 w-24" rounded="md" />
          <Skeleton className="h-11 w-full" rounded="xl" />
        </div>
      ))}
      <Skeleton className="h-11 w-32 mt-2" rounded="full" />
    </div>
  );
}

export function ProfileRouteSkeleton() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:gap-8 items-start">
        <aside className="flex flex-col gap-6">
          <div className="bg-gray-100 rounded-2xl shadow-card p-6 lg:p-7 flex items-center gap-5 lg:flex-col lg:text-center lg:gap-4">
            <Skeleton className="w-20 h-20 lg:w-32 lg:h-32 shrink-0" rounded="full" />
            <div className="min-w-0 flex-1 lg:w-full flex flex-col gap-2">
              <Skeleton className="h-6 w-40 max-w-full mx-0 lg:mx-auto" rounded="md" />
              <Skeleton className="h-4 w-52 max-w-full mx-0 lg:mx-auto" rounded="md" />
              <Skeleton className="h-4 w-24 mx-0 lg:mx-auto" rounded="md" />
            </div>
          </div>

          <div className="hidden lg:flex flex-col bg-gray-100 rounded-2xl shadow-card p-2 gap-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full" rounded="xl" />
            ))}
          </div>
        </aside>

        <div className="flex flex-col gap-6">
          <section className="bg-gray-100 rounded-2xl shadow-card p-6 lg:p-8 flex flex-col gap-5">
            <div className="flex flex-col gap-2 mb-1">
              <Skeleton className="h-6 w-52" rounded="md" />
              <Skeleton className="h-4 w-72 max-w-full" rounded="md" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
              <Skeleton className="h-11 md:col-span-12" rounded="xl" />
              <Skeleton className="h-11 md:col-span-6" rounded="xl" />
              <Skeleton className="h-11 md:col-span-6" rounded="xl" />
            </div>

            <Skeleton className="h-14 w-full" rounded="2xl" />

            <div className="pt-4 mt-2 border-t border-line flex flex-col gap-4 md:gap-5">
              <Skeleton className="h-5 w-44" rounded="md" />
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
                <Skeleton className="h-11 md:col-span-4" rounded="xl" />
                <Skeleton className="h-11 md:col-span-8" rounded="xl" />
                <Skeleton className="h-11 md:col-span-4" rounded="xl" />
                <Skeleton className="h-11 md:col-span-8" rounded="xl" />
                <Skeleton className="h-11 md:col-span-4" rounded="xl" />
                <Skeleton className="h-11 md:col-span-5" rounded="xl" />
                <Skeleton className="h-11 md:col-span-3" rounded="xl" />
              </div>
            </div>

            <div className="flex justify-end pt-4 mt-2 border-t border-line">
              <Skeleton className="h-11 w-full sm:w-[200px]" rounded="full" />
            </div>
          </section>

          <section className="lg:hidden bg-gray-100 rounded-2xl shadow-card p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-5 w-32" rounded="md" />
                <Skeleton className="h-4 w-44" rounded="md" />
              </div>
              <Skeleton className="h-9 w-24 shrink-0" rounded="xl" />
            </div>
          </section>

          <section className="lg:hidden bg-gray-100 rounded-2xl shadow-card p-6 flex flex-col gap-4">
            <Skeleton className="h-5 w-16" rounded="md" />
            <Skeleton className="h-11 w-full" rounded="xl" />
            <div className="pt-3 border-t border-line flex items-center justify-between gap-4">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-12" rounded="md" />
                <Skeleton className="h-3 w-44" rounded="md" />
              </div>
              <Skeleton className="h-9 w-28 shrink-0" rounded="xl" />
            </div>
          </section>

          <section className="lg:hidden bg-gray-100 rounded-2xl shadow-card p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-24" rounded="md" />
                <Skeleton className="h-3 w-52" rounded="md" />
              </div>
              <Skeleton className="h-9 w-20 shrink-0" rounded="xl" />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function NewListingRouteSkeleton() {
  return (
    <main className="max-w-7xl mx-auto px-4 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_320px] gap-6 lg:gap-8 items-start">
        {/* Desktop: progress sidebar */}
        <div className="hidden lg:flex flex-col gap-4 sticky top-24">
          <div className="bg-white rounded-3xl border border-line p-6 shadow-sm">
            <Skeleton className="h-5 w-24 mb-6" rounded="md" />
            <div className="flex flex-col gap-5 relative">
              <div className="absolute left-3.5 top-2 bottom-2 w-px bg-line -z-10" />
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="w-7 h-7 shrink-0" rounded="full" />
                  <Skeleton className="h-3.5 w-24" rounded="md" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: progress bar */}
        <div className="lg:hidden flex flex-col gap-2.5 mb-1 px-1">
          <div className="flex items-center justify-between">
            <Skeleton className="h-3.5 w-24" rounded="md" />
            <Skeleton className="h-3.5 w-20" rounded="md" />
          </div>
          <div className="h-2 w-full bg-line/80 rounded-full overflow-hidden">
            <Skeleton className="h-full w-1/12" rounded="full" />
          </div>
        </div>

        {/* Form section card (mirrors FormSection + step 1 body) */}
        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-3xl border border-line p-6 shadow-card">
            <div className="flex items-center gap-3 mb-5">
              <Skeleton className="w-10 h-10 shrink-0" rounded="2xl" />
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-4 w-28" rounded="md" />
                <Skeleton className="h-3 w-40" rounded="md" />
              </div>
            </div>

            <div className="flex flex-col gap-6">
              {/* Tipo de negócio: 2 cards */}
              <div>
                <Skeleton className="h-3.5 w-32 mb-3" rounded="md" />
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 rounded-2xl border-2 border-line">
                      <Skeleton className="w-9 h-9 shrink-0" rounded="xl" />
                      <div className="flex flex-col gap-1.5 flex-1">
                        <Skeleton className="h-3.5 w-16" rounded="md" />
                        <Skeleton className="h-3 w-24" rounded="md" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Categoria: 3 cards */}
              <div>
                <Skeleton className="h-3.5 w-20 mb-3" rounded="md" />
                <div className="grid grid-cols-3 gap-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex flex-col items-center gap-2.5 py-4 px-3 rounded-2xl border-2 border-line">
                      <Skeleton className="w-9 h-9" rounded="xl" />
                      <Skeleton className="h-3 w-14" rounded="md" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t border-line mt-2">
                <Skeleton className="h-11 w-32" rounded="full" />
              </div>
            </div>
          </div>
        </div>

        {/* Right column: preview + revisar button */}
        <div className="flex flex-col gap-6 lg:sticky lg:top-24">
          <div className="hidden lg:flex flex-col gap-3">
            <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-card">
              <div className="w-full relative" style={{ paddingTop: '66%' }}>
                <Skeleton className="absolute inset-0 w-full h-full" rounded="none" />
              </div>
              <div className="flex flex-col gap-2 p-4">
                <Skeleton className="h-3 w-32" rounded="md" />
                <Skeleton className="h-3 w-20" rounded="md" />
                <Skeleton className="h-6 w-28 mt-1" rounded="md" />
                <div className="flex justify-center gap-2 mt-2">
                  <Skeleton className="h-7 w-12" rounded="lg" />
                  <Skeleton className="h-7 w-12" rounded="lg" />
                  <Skeleton className="h-7 w-12" rounded="lg" />
                  <Skeleton className="h-7 w-14" rounded="lg" />
                </div>
              </div>
            </div>
          </div>
          <Skeleton className="hidden lg:block h-12 w-full" rounded="2xl" />
        </div>
      </div>
    </main>
  );
}

function PremiumRouteSkeleton() {
  return (
    <main className={`${pageShell} pt-4 sm:pt-6 pb-10 sm:pb-16`}>
      <div className="grid lg:grid-cols-[minmax(0,3fr)_minmax(0,9fr)] gap-10 lg:gap-14 lg:items-center">
        {/* Coluna esquerda: hero + destaques (só desktop) */}
        <div className="flex flex-col items-center lg:items-start gap-3 max-w-2xl mx-auto lg:mx-0 w-full">
          <Skeleton rounded="full" className="h-6 w-24" />
          <Skeleton rounded="md" className="h-10 w-80 max-w-full" />
          <Skeleton rounded="md" className="h-4 w-72 max-w-full" />
          <div className="hidden lg:flex flex-col gap-5 mt-8 w-full">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3.5">
                <Skeleton rounded="lg" className="h-11 w-11 shrink-0" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <Skeleton rounded="md" className="h-4 w-32" />
                  <Skeleton rounded="md" className="h-3 w-full max-w-xs" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Coluna direita: cards de preço */}
        <div className="grid gap-5 sm:grid-cols-2 items-start max-w-3xl mx-auto w-full lg:max-w-none lg:mx-0">
          {Array.from({ length: 2 }).map((_, card) => (
            <div key={card} className="bg-white rounded-3xl shadow-card p-7 flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <Skeleton rounded="md" className="h-3 w-20" />
                <Skeleton rounded="md" className="h-10 w-32" />
              </div>
              <div className="flex flex-col gap-3">
                {Array.from({ length: card === 0 ? 4 : 6 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Skeleton rounded="full" className="h-4 w-4 mt-0.5 shrink-0" />
                    <Skeleton rounded="md" className="h-4 flex-1" />
                  </div>
                ))}
              </div>
              <Skeleton rounded="full" className="h-[48px] w-full mt-auto" />
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

function MapSkeleton() {
  return <Skeleton className="h-full w-full" rounded="none" />;
}

function AuthSkeleton() {
  return (
    <div className="max-w-sm mx-auto px-4 py-10 flex flex-col gap-4">
      <Skeleton className="h-7 w-32 mx-auto" rounded="md" />
      <Skeleton className="h-11 w-full" rounded="xl" />
      <Skeleton className="h-11 w-full" rounded="xl" />
      <Skeleton className="h-11 w-full mt-2" rounded="full" />
    </div>
  );
}

function GenericSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex flex-col gap-3">
      <Skeleton className="h-7 w-1/2" rounded="md" />
      <Skeleton className="h-4 w-full" rounded="md" />
      <Skeleton className="h-4 w-11/12" rounded="md" />
      <Skeleton className="h-4 w-10/12" rounded="md" />
    </div>
  );
}
